import { AWARD_TASK_ID_PREFIX, awardPeriodFromTaskId, recognizedAwardMonthLabel } from './studentAwards';
import { offeringFeedHref } from './offerings';
import type { TaskDoc } from './tasks';

export interface TaskSourceMeta {
  eyebrow: string;
  title: string;
  label: string;
  actionLabel: string;
  href: string;
  preview: string;
}

function previewText(source: NonNullable<TaskDoc['source']>): string {
  return source.preview?.trim() ?? '';
}

export function taskSourceMeta(source: TaskDoc['source']): TaskSourceMeta | null {
  if (!source) return null;
  if (source.type === 'message') {
    return {
      eyebrow: 'Message trail',
      title: 'From a staff message',
      label: 'Open message',
      actionLabel: 'Open message trail',
      href: `/messages/${source.threadId}#message-${source.messageId}`,
      preview: previewText(source),
    };
  }
  if (source.type === 'discussion') {
    return {
      eyebrow: 'Staff announcement',
      title: 'From a staff announcement',
      label: 'Open announcement',
      actionLabel: 'Open announcement',
      // Staff announcements (the old Studio Board) live in the merged
      // Messages list; the row there carries the #board-{id} anchor.
      href: `/messages#board-${source.discussionId}`,
      preview: previewText(source),
    };
  }
  if (source.type === 'classPost') {
    return {
      eyebrow: 'Class or dance board',
      title: 'From a class post',
      label: 'Open class post',
      actionLabel: 'Go to class page',
      href: offeringFeedHref(source.offeringId, source.postId),
      preview: previewText(source),
    };
  }
  if (source.type === 'bulletin') {
    return {
      eyebrow: 'Announcement',
      title: 'From an announcement',
      label: 'Open announcement',
      actionLabel: 'Open announcement',
      href: `/bulletins#bulletin-${source.bulletinId}`,
      preview: previewText(source),
    };
  }
  return null;
}

/**
 * A task's real deep-link destination, following the explicit `source`
 * field when there is one and otherwise falling back to whatever a task's
 * own deterministic doc id proves about it. The monthly Student Awards
 * reminder tasks are the one case of that today: they carry no `source`,
 * but their id encodes the exact round they belong to, so an overdue one
 * can still land a teacher on ITS month instead of whatever the Student
 * Awards page would default to that day.
 */
export function taskActionMeta(task: Pick<TaskDoc, 'id' | 'source'>): TaskSourceMeta | null {
  const explicit = taskSourceMeta(task.source ?? null);
  if (explicit) return explicit;
  if (task.id.startsWith(AWARD_TASK_ID_PREFIX)) {
    const period = awardPeriodFromTaskId(task.id);
    if (period) {
      // The month the round RECOGNIZES; the link still carries the stored key.
      const label = recognizedAwardMonthLabel(period);
      return {
        eyebrow: 'Monthly award pick',
        title: `${label} nomination`,
        label: 'Open Student Awards',
        actionLabel: `Open Student Awards for ${label}`,
        href: `/student-awards?period=${period}`,
        preview: '',
      };
    }
  }
  return null;
}
